import { useState, useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { NotificationState } from '../types/common';
import { takeRecentHold } from '../api/client';

// Replaces the `const [notification, setNotification] = useState(null)` +
// overlay JSX that 18 modules each carried their own copy of.
//
//   const { notification, notify, notifyError, dismiss } = useNotification();
//   ...
//   <Notification notification={notification} onClose={dismiss} />

export type NotificationType = 'success' | 'error';

/** Shown instead of a screen's "saved / deleted / updated" when the save was held for approval. */
export const HELD_MESSAGE = 'Sent for approval — not done yet. It happens when the last approver says yes.';

/** Signature every module passes to the shared ledger hooks as `notify`. */
export type Notify = (message: string, type?: NotificationType) => void;

export interface UseNotificationResult {
  notification: NotificationState | null;
  notify: Notify;
  notifySuccess: (message: string) => void;
  notifyError: (message: string) => void;
  dismiss: () => void;
  setNotification: Dispatch<SetStateAction<NotificationState | null>>;
}

export const useNotification = (): UseNotificationResult => {
  const [notification, setNotification] = useState<NotificationState | null>(null);

  const dismiss = useCallback(() => setNotification(null), []);

  const notify = useCallback<Notify>((message, type = 'success') => {
    // A save just held for approval has not happened yet, whatever the screen says.
    if (type === 'success' && takeRecentHold()) message = HELD_MESSAGE;
    setNotification({ message, type });
  }, []);

  const notifySuccess = useCallback((message: string) => notify(message, 'success'), [notify]);
  const notifyError = useCallback((message: string) => notify(message, 'error'), [notify]);

  return { notification, notify, notifySuccess, notifyError, dismiss, setNotification };
};

export default useNotification;
